import React, { useState, useEffect } from 'react';
import { ArrowRight, Phone, CheckCircle, Zap, User, Mail, AlertCircle } from 'lucide-react';
import { useProgress } from '../../../context/ProgressContext';
import { useAnimatedNumber } from '../../../hooks/useAnimatedNumber';
import { waitlistService } from '../../../services/waitlistService';

const HeroSection = () => {
  const { joinedMembers, spotsLeft, progressPercentage } = useProgress();
  const animatedJoined = useAnimatedNumber(joinedMembers, 1500);
  const animatedSpotsLeft = useAnimatedNumber(spotsLeft, 1500);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Add to Supabase database
      const { data, error: dbError } = await waitlistService?.addEntry({
        fullName: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        source: 'hero_form'
      });

      if (dbError) {
        setError(dbError?.message || 'Failed to join waitlist. Please try again.');
        return;
      }

      // Also send to Google Sheets (structure for future implementation)
      try {
        await waitlistService?.sendToGoogleSheets(formData);
      } catch (sheetsError) {
        // Google Sheets error doesn't prevent success since data is in database console.log('Google Sheets integration pending:', sheetsError);
      }

      setIsSubmitted(true);
      setError(null);
      
      // Clear form data
      setFormData({
        fullName: '',
        email: '',
        phone: ''
      });

    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when user wants to submit again
  const resetForm = () => {
    setIsSubmitted(false);
    setError(null);
    setFormData({
      fullName: '',
      email: '',
      phone: ''
    });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: '#007BFF'
      }}>
      {/* Background Decoration with subtle blue effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-400/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-40 right-16 w-24 h-24 bg-blue-300/10 rounded-full blur-lg"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-blue-500/5 rounded-full blur-md"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Hero Content */}
          <div className="text-center lg:text-left lg:order-1">
            {/* Main Headline - Exactly as specified in attachment */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="block">Find a Trusted Pro</span>
              <span className="block" style={{ color: '#F97316' }}>When You Need</span>
              <span className="block">Them Most.</span>
            </h1>

            {/* Tagline */}
            <div className="mb-6">
              <p className="text-xl md:text-2xl text-white font-semibold">Trusted Pros. Anytime, Every Time.</p>
            </div>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white mb-8 leading-relaxed opacity-90">
              Fast emergency help — usually within 2 hours, even after hours. Join Tampa's verified network of licensed pros free for a limited time.
            </p>

            {/* Mobile Form Placement - Show only on mobile, hide on desktop */}
            <div className="block lg:hidden mb-8">
              {isSubmitted ? (
              /* Success State - Mobile */
              (<div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Welcome to ProConnect!
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Thank you {formData?.fullName?.split(' ')?.[0] || 'for joining'}! You are now on our early access list. We will notify you when ProConnect launches in Tampa.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-gray-500 text-sm">
                    Check your email for confirmation and updates.
                  </p>
                </div>
                {/* Reset Button */}
                <button
                  onClick={resetForm}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  Join another person →
                </button>
                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <span className="text-green-500">✅</span>
                      <span>Licensed & Insured</span>
                    </div>
                    <div className="text-gray-300">|</div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-500">✅</span>
                      <span>Background Checked</span>
                    </div>
                  </div>
                </div>
              </div>)) : (

              /* Registration Form - Mobile */
              (<div className="bg-white rounded-2xl p-8 shadow-2xl">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Get Free Lifetime Access — Join the Waitlist.
                  </h3>
                  <p className="text-gray-600">
                    Get early access when we launch in Tampa Bay
                  </p>
                </div>
                {/* Error Display */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                      Full Name
                    </label>
                    <input
                    type="text"
                    name="fullName"
                    value={formData?.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="Enter your full name" />

                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                      Email Address
                    </label>
                    <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="Enter your email" />

                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                      Phone Number
                    </label>
                    <input
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                    placeholder="Enter your phone number" />

                  </div>

                  {/* Submit Button */}
                  <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white font-bold px-6 py-4 rounded-lg transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                  style={{
                    backgroundColor: '#F97316'
                  }}
                  onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = '#E35C0C')}
                  onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = '#F97316')}>

                    {isLoading ?
                  <span className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Joining Waitlist...
                      </span> :

                  <span className="flex items-center justify-center gap-3">
                        Get Free Lifetime Access — Join the Waitlist
                        <ArrowRight className="w-5 h-5" />
                      </span>
                  }
                  </button>
                  
                  {/* Trust Badges - Updated with three badges */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-center gap-1 text-xs text-gray-600 flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-green-500">✅</span>
                        <span>Licensed & Insured</span>
                      </div>
                      <div className="text-gray-300">|</div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-500">✅</span>
                        <span>Background Checked</span>
                      </div>
                      <div className="text-gray-300">|</div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-500">✅</span>
                        <span>Local Tampa Pros</span>
                      </div>
                    </div>
                  </div>
                </form>
              </div>))
              }
            </div>

            {/* Progress Bar - Updated background color */}
            <div className="mb-8">
              <div
                className="rounded-2xl px-6 py-6 border"
                style={{
                  backgroundColor: '#1E40AF',
                  borderColor: 'rgba(30, 64, 175, 0.3)'
                }}>
                <div className="text-white font-bold text-lg mb-4 text-center lg:text-left">
                  <span className="flex items-center justify-center lg:justify-start gap-2">
                    <Zap className="w-5 h-5 text-orange-500" />
                    Only 1000 Founding Members Will Get Lifetime Free Access — {animatedSpotsLeft} Spots Left
                  </span>
                </div>
                <div
                  className="w-full rounded-full h-3 relative overflow-hidden mb-4"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                  <div
                    className="h-3 rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${progressPercentage}%`,
                      backgroundColor: '#F97316'
                    }}>
                  </div>
                </div>
                <div className="flex justify-between text-white font-medium">
                  <span>{animatedJoined} joined</span>
                  <span>{animatedSpotsLeft} spots remaining</span>
                </div>
              </div>
            </div>

            {/* Stats with Checkmarks */}
            <div className="flex justify-center lg:justify-start">
              <div className="flex items-center gap-6 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-green-400 text-xl">✅</span>
                  <span className="font-semibold">505 Verified Pros</span>
                </div>
                <div className="text-white/50">|</div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400 text-xl">📍</span>
                  <span className="font-semibold">4 Cities Launching</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - CTA Form (Desktop Only) */}
          <div className="w-full max-w-md mx-auto lg:max-w-none hidden lg:block lg:order-2">
            {isSubmitted ? (
            /* Success State - Desktop */
            (<div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Welcome to ProConnect!
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thank you {formData?.fullName?.split(' ')?.[0] || 'for joining'}! You are now on our early access list. We will notify you when ProConnect launches in Tampa.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-500 text-sm">
                  Check your email for confirmation and updates.
                </p>
              </div>
              {/* Reset Button */}
              <button
                onClick={resetForm}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm mb-6"
              >
                Join another person →
              </button>
              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="text-green-500">✅</span>
                    <span>Licensed & Insured</span>
                  </div>
                  <div className="text-gray-300">|</div>
                  <div className="flex items-center gap-1">
                    <span className="text-green-500">✅</span>
                    <span>Background Checked</span>
                  </div>
                </div>
              </div>
            </div>)) : (

            /* Registration Form - Desktop */
            (<div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Get Free Lifetime Access — Join the Waitlist.
                </h3>
                <p className="text-gray-600">
                  Get early access when we launch in Tampa Bay
                </p>
              </div>
              {/* Error Display */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                    Full Name
                  </label>
                  <input
                  type="text"
                  name="fullName"
                  value={formData?.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                  placeholder="Enter your full name" />

                </div>

                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                    Email Address
                  </label>
                  <input
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                  placeholder="Enter your email" />

                </div>

                {/* Phone Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-6 h-6 inline mr-2" style={{ color: '#F97316' }} />
                    Phone Number
                  </label>
                  <input
                  type="tel"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                  placeholder="Enter your phone number" />

                </div>

                {/* Submit Button */}
                <button
                type="submit"
                disabled={isLoading}
                className="w-full text-white font-bold px-6 py-4 rounded-lg transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                style={{
                  backgroundColor: '#F97316'
                }}
                onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = '#E35C0C')}
                onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = '#F97316')}>

                  {isLoading ?
                <span className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Joining Waitlist...
                    </span> :

                <span className="flex items-center justify-center gap-3">
                      Get Free Lifetime Access — Join the Waitlist
                      <ArrowRight className="w-5 h-5" />
                    </span>
                }
                </button>
                
                {/* Trust Badges - Updated with three badges */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-600 flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-green-500">✅</span>
                      <span>Licensed & Insured</span>
                    </div>
                    <div className="text-gray-300">|</div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-500">✅</span>
                      <span>Background Checked</span>
                    </div>
                    <div className="text-gray-300">|</div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-500">✅</span>
                      <span>Local Tampa Pros</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>))
            }
          </div>
        </div>
      </div>
    </section>
  );

};

export default HeroSection;